import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import { toast } from "react-toastify";

import { useNavigate } from "react-router-dom";

import { apiAxios } from "../../utils/apiAxios";
import DataTable from "react-data-table-component";
interface PendingCase {
  caseID: number;
  orderID: string;
  patientName: string;
  totalAmount: number;
  createdDate: string;
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;

const RAZORPAY_KEY =
  process.env.REACT_APP_RAZORPAY_KEY || "";

const PaymentRetryPage = () => {
  const navigate = useNavigate();

  const [loading, setLoading] =
    useState(false);

  const [pendingCases, setPendingCases] =
    useState<PendingCase[]>([]);

  const paymentCompletedRef =
    useRef(false);

  const isVerifyingRef =
    useRef(false);

  // =========================
  // LOAD RAZORPAY SDK
  // =========================
  const loadRazorpay = () => {
    return new Promise((resolve) => {
      const existingScript =
        document.getElementById(
          "razorpay-sdk"
        );

      if (existingScript) {
        resolve(true);
        return;
      }

      const script =
        document.createElement("script");

      script.id = "razorpay-sdk";

      script.src =
        "https://checkout.razorpay.com/v1/checkout.js";

      script.onload = () => resolve(true);

      script.onerror = () => resolve(false);

      document.body.appendChild(script);
    });
  };

  // =========================
  // FETCH PENDING CASES
  // =========================
  const fetchPendingCases =
    async () => {
      try {
        setLoading(true);

        const clinicId =
          sessionStorage.getItem(
            "selectedClinicId"
          );

        const token =
          sessionStorage.getItem(
            "token"
          );

        const response =
          await apiAxios.post(
            `${API_BASE_URL}/Dentist/GetPaymentPendingOrders`,
            {
              clinicId,
            },
            {
              headers: {
                AuthorizationData:
                  token,
                "Content-Type":
                  "application/json",
              },
            }
          );

        const data = Array.isArray(
          response?.data?.singleData
        )
          ? response.data.singleData
          : [];

        const sortedData = [...data].sort(
          (a: PendingCase, b: PendingCase) =>
            a.caseID - b.caseID
        );

        setPendingCases(sortedData);

        //    setPendingCases(data);
      } catch (error) {
        console.error(error);

        toast.error(
          "Failed to fetch pending payments"
        );

        setPendingCases([]);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchPendingCases();
  }, []);

  // =========================
  // SAVE FAILED PAYMENT
  // =========================
  const saveFailedPayment =
    async (payload: any) => {
      try {
        const token =
          sessionStorage.getItem(
            "token"
          ) || "";

        await apiAxios.post(
          `${API_BASE_URL}/payment/payment-failed`,
          payload,
          {
            headers: {
              AuthorizationData:
                token,
            },
          }
        );
      } catch (err) {
        console.error(
          "Failed payment save error",
          err
        );
      }
    };

  // =========================
  // VERIFY PAYMENT
  // =========================
  const verifyPayment =
    async (
      response: any,
      dbPaymentId: number,
      caseID: number
    ) => {
      if (
        isVerifyingRef.current
      ) {
        return;
      }

      isVerifyingRef.current =
        true;

      try {
        setLoading(true);

        const token =
          sessionStorage.getItem(
            "token"
          ) || "";

        const res =
          await apiAxios.post(
            `${API_BASE_URL}/payment/verify`,
            {
              paymentDbId:
                dbPaymentId,

              razorpayOrderId:
                response.razorpay_order_id,

              razorpayPaymentId:
                response.razorpay_payment_id,

              razorpaySignature:
                response.razorpay_signature,

              caseIDs: [caseID],
            },
            {
              headers: {
                AuthorizationData:
                  token,
              },
            }
          );

        if (res.data) {
          toast.success(
            res.data?.message ||
            "Payment Successful"
          );

          fetchPendingCases();

          setTimeout(() => {
            navigate(0);
          }, 1000);
        } else {
          throw new Error(
            "Payment verification failed"
          );
        }
      } catch (err: any) {
        console.error(err);

        const errorMessage =
          err?.response?.data
            ?.message ||
          err?.message ||
          "Payment verification failed";

        toast.error(errorMessage);

        await saveFailedPayment({
          paymentDbId:
            dbPaymentId,

          errorDescription:
            errorMessage,
        });
      } finally {
        setLoading(false);

        isVerifyingRef.current =
          false;
      }
    };

  // =========================
  // CREATE ORDER
  // =========================
  const createRazorpayOrder =
    async (item: PendingCase) => {
      const token =
        sessionStorage.getItem(
          "token"
        ) || "";

      const selectedClinic =
        JSON.parse(
          sessionStorage.getItem(
            "selectedClinic"
          ) || "{}"
        );

      const res =
        await apiAxios.post(
          `${API_BASE_URL}/payment/create-order`,
          {
            amount:
              item.totalAmount,
               originalAmount: item.totalAmount,

            email:
              selectedClinic.emailID ||
              "test@gmail.com",

            contact:
              selectedClinic.deliveryContact,

            CaseIDs: [
              item.caseID,
            ],
          },
          {
            headers: {
              AuthorizationData:
                token,
            },
          }
        );

      return res.data;
    };

  // =========================
  // OPEN RAZORPAY
  // =========================
  const handleRetryPayment =
    async (
      item: PendingCase
    ) => {
      const loaded =
        await loadRazorpay();

      if (!loaded) {
        toast.error(
          "Razorpay SDK failed to load"
        );

        return;
      }

      try {
        setLoading(true);

        paymentCompletedRef.current =
          false;

        const order =
          await createRazorpayOrder(
            item
          );

        const selectedClinic =
          JSON.parse(
            sessionStorage.getItem(
              "selectedClinic"
            ) || "{}"
          );

        const options = {
          key: RAZORPAY_KEY,

          amount:
            order.amount * 100,

          currency: "INR",

          name:
            "TOOTHZAP PRIVATE LIMITED",

          description: `Case ID: TZ${item.caseID}`,

          order_id:
            order.orderId,

          handler:
            async function (
              response: any
            ) {
              paymentCompletedRef.current =
                true;

              await verifyPayment(
                response,
                order.dbPaymentId,
                item.caseID
              );
            },

          prefill: {
            name:
              selectedClinic.clinicName,

            email:
              selectedClinic.emailID,

            contact:
              selectedClinic.deliveryContact,
          },

          notes: {
            CaseID: `TZ${item.caseID}`,
          },

          theme: {
            color: "#0d6efd",
          },

          modal: {
            escape: true,

            backdropclose:
              false,

            ondismiss:
              async function () {
                if (
                  paymentCompletedRef.current
                ) {
                  return;
                }

                await saveFailedPayment(
                  {
                    paymentDbId:
                      order.dbPaymentId,

                    errorDescription:
                      "User closed Razorpay popup",
                  }
                );

                toast.error(
                  "Payment popup closed"
                );

                setLoading(
                  false
                );
              },
          },
        };

        const rzp =
          new window.Razorpay(
            options
          );

        rzp.on(
          "payment.failed",
          async function (
            response: any
          ) {

            rzp.close();

            await saveFailedPayment(
              {
                paymentDbId:
                  order.dbPaymentId,

                razorpayOrderId:
                  response.error
                    .metadata
                    .order_id,

                razorpayPaymentId:
                  response.error
                    .metadata
                    .payment_id,

                errorCode:
                  response.error
                    .code,

                errorDescription:
                  response.error
                    .description,

                errorSource:
                  response.error
                    .source,

                errorStep:
                  response.error
                    .step,

                errorReason:
                  response.error
                    .reason,
              }
            );

            toast.error(
              response.error
                .description ||
              "Payment Failed"
            );

            setLoading(false);
          }
        );

        rzp.open();
      } catch (err) {
        console.error(err);

        toast.error(
          "Payment initialization failed"
        );

        setLoading(false);
      }
    };
  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString(
      "en-GB",
      {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      }
    );
  };



  const columns = [
    {
      name: "Case ID",
      selector: (row: PendingCase) => row.caseID,
      sortable: true,
    },
    {
      name: "Order ID",
      selector: (row: PendingCase) => row.orderID,
      sortable: true,
    },
    {
      name: "Patient",
      selector: (row: PendingCase) => row.patientName,
      sortable: true,
    },
    {
      name: "Amount",
      cell: (row: PendingCase) => `₹ ${row.totalAmount}`,
      sortable: true,
    },
    {
      name: "Created Date",
      selector: (row: PendingCase) => formatDateTime(row.createdDate),
      sortable: true,
    },
    {
      name: "Action",
      cell: (row: PendingCase) => (
        <button
          className="btn btn-primary btn-sm"
          disabled={loading}
          onClick={() => handleRetryPayment(row)}
        >
          Retry Payment
        </button>
      ),
    },
  ];

  return (
    <div className="container-fluid mt-2">


      {/* HEADER */}
      <div className="d-flex justify-content-between pb-3">
        <h4 className="mb-0">
          Pending Payments
        </h4>

        <button
          className="btn btn-outline-primary"
          onClick={
            fetchPendingCases
          }
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {/* BODY */}
      <div className="premium-card p-3">

        {/* LOADER */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" />
          </div>
        )}

        {/* EMPTY */}
        {!loading &&
          pendingCases.length ===
          0 && (
            <div className="alert alert-warning m-3">
              No pending payments
              found.
            </div>
          )}

        {/* TABLE */}
        {!loading &&
          pendingCases.length >
          0 && (
            <div
              style={{
                width: "100%",
                overflowX:
                  "scroll",
              }}
            >
              <DataTable
                columns={columns}
                data={pendingCases}
                pagination
                highlightOnHover
                striped
                responsive
                fixedHeader
                fixedHeaderScrollHeight="500px"
                className="admin-delivery-table"
              />
            </div>
          )}
        <style>
          {`
    /* Card */
    .shadow-container {
      background: #fff;
      border-radius: 24px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    }

    /* Table Wrapper */
    .custom-table-wrapper {
      width: 100%;
      overflow-x: auto;
      border-radius: 18px;
      background: #fff;
    }

    /* Table */
    .custom-table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      min-width: 1400px;
    }

    /* Header */
    .custom-table thead th {
      background: #fff;
      color: #1f2937;
      font-size: 15px;
      font-weight: 700;
      padding: 22px 24px;
      border-bottom: 1px solid #e5e7eb;
      white-space: nowrap;
    }

    .custom-table thead th:first-child {
      border-top-left-radius: 14px;
    }

    .custom-table thead th:last-child {
      border-top-right-radius: 14px;
    }

    /* Rows */
    .custom-table tbody tr {
      background: #fafafa;
    }
  .custom-table thead th{
      border-bottom: none!important;
  }
    .custom-table tbody td {
      padding: 20px 24px;
      vertical-align: middle;
      border-bottom: 1px solid #e5e7eb;
      color: #374151;
      font-size: 14px;
      white-space: nowrap;
          border-right: none;
    border-left: none;
    }

    .custom-table tbody tr:hover {
      background: #f4f6f8;
    }

    /* Status Badge */
    .status-badge {
      background: #fff3cd;
      color: #856404;
      padding: 8px 14px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 600;
      display: inline-block;
    }

    /* Action Buttons */
    .action-btns {
      display: flex;
      gap: 8px;
    }

    .action-btns .btn-success,
    .action-btns .btn-danger {
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      padding: 6px 14px;
    }

    /* Modal */
    .modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 9999;
    }

    .custom-modal {
      width: 450px;
      background: #fff;
      border-radius: 16px;
      padding: 24px;
      box-shadow: 0 10px 30px rgba(0,0,0,0.15);
    }

    .refund-summary-box {
      background: #f8f9fb;
      border-radius: 12px;
      padding: 15px;
      margin-top: 15px;
    }

    .refund-summary-box div {
      display: flex;
      justify-content: space-between;
      margin-bottom: 10px;
    }

    .refund-summary-box div:last-child {
      margin-bottom: 0;
    }
  `}
        </style>
      </div>

    </div>
  );
};

export default PaymentRetryPage;